import { useDispatch } from "react-redux";
import { useEffect, useMemo } from "react";
import { BroadcastResponse as CoreBroadcastResponse } from "@switcherstudio/api-core-client";
import { ModalBase } from "../ModalBase";
import { Wizard } from "components/wizards/Wizard";
import { PlatformSettings } from "components/modal/NewLivestreamModal/steps/PlatformSettings";
import { SelectExternalDestinationStep } from "./steps/SelectExternalDestination";
import { AddDestinationsProcessingStep } from "./steps/Processing";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { resetAllForms, updateFormState, updateGeneralForm } from "store/platforms/slice";
import { ScheduledOptions } from "views/page-content/platforms/platform/forms/types";
import { getStreamQualityFromBroadcastResponse } from "helpers/resolution";
import { QualitySelection } from "store/platforms/types";
import { mapBroadcastResponseToBroadcast } from "helpers/mappers/mapBroadcastResponseToBroadcast";
import { useAddDestinationsStepLogic } from "./useAddDestinationsStepLogic";
import { AddDestinationsModalSteps } from "./types";
import { getConnectedDestinationDescriptors } from "./helpers";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { Spinner } from "components/spinners/Spinner";

export interface AddDestinationsModalProps {
    broadcast: CoreBroadcastResponse;
    // Called when the modal should fully close - caller refetches the broadcast.
    onClose: () => void;
    // Refetches the broadcast - also called when in-flight connections settle after close.
    refetchBroadcast?: () => void;
}

// Modal for adding destinations to an already scheduled livestream (sc-56801).
export const AddDestinationsModal = ({
    broadcast,
    onClose,
    refetchBroadcast
}: AddDestinationsModalProps) => {
    const dispatch = useDispatch();

    // Needed to resolve the original channel id for already-connected RTMP destinations.
    const { data: userEditableRtmpChannels, loading: rtmpChannelsLoading } =
        useSwitcherClient(
            (client) =>
                client.switcherStreamSettings_GetAllSwitcherStudioSettings,
            { suppressErrorBoundary: true, requestImmediately: true }
        );

    const existingDestinations = useMemo(
        () =>
            getConnectedDestinationDescriptors(
                broadcast?.StreamSettings,
                userEditableRtmpChannels
            ),
        [broadcast?.StreamSettings, userEditableRtmpChannels]
    );

    const existingDestinationIds = useMemo(
        () => new Set(existingDestinations.map((d) => d.id)),
        [existingDestinations]
    );

    const {
        activeStep,
        setActiveStep,
        selectedPlatform,
        setSelectedPlatform,
        selectedDestinationId,
        setSelectedDestinationId,
        setCurrentFacebookEdge,
        setHasAddedPlatformSpecificSettings,
        setOnAdvanceStep,
        setOnDismiss,
        advanceStep,
        advanceButtonText,
        dismissButtonAction,
        dismissButtonText,
        canProceedToNextStep,
        modalHeaderText,
        newlySelectedDestinations,
        setSubmissionStatus
    } = useAddDestinationsStepLogic({ existingDestinationIds, onClose });

    const mappedBroadcast = useMemo(
        () => mapBroadcastResponseToBroadcast(broadcast),
        [broadcast]
    );

    // Clear stale platform state, then seed the draft selection and generalForm from the broadcast.
    useEffect(() => {
        if (rtmpChannelsLoading) return;

        dispatch(resetAllForms());
        dispatch(updateFormState(ScheduledOptions.Later));
        dispatch(
            updateGeneralForm({
                title: mappedBroadcast?.Title ?? "",
                description: mappedBroadcast?.Description ?? "",
                scheduledStartTime: mappedBroadcast?.StartsAt
                    ? new Date(mappedBroadcast.StartsAt)
                    : new Date(Date.now() + 30 * 60 * 1000),
                selectedQuality: getStreamQualityFromBroadcastResponse(
                    broadcast
                ) as QualitySelection,
                selectedSimulcastDestinations: existingDestinations
            })
        );

        return () => {
            dispatch(resetAllForms());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rtmpChannelsLoading]);

    return (
        <ModalBase
            isOpen
            header={modalHeaderText}
            successButton={rtmpChannelsLoading ? null : advanceButtonText}
            onSuccess={advanceStep}
            dismissButton={rtmpChannelsLoading ? null : dismissButtonText}
            onDismiss={dismissButtonAction}
            successDisabled={!canProceedToNextStep}
            shouldNotCloseOnSuccess={true}
        >
            <div>
                {rtmpChannelsLoading ? (
                    <Spinner />
                ) : (
                    <Wizard<AddDestinationsModalSteps>
                        activeStep={activeStep}
                        steps={[
                            {
                                id: AddDestinationsModalSteps.SelectDestination,
                                component: (
                                    <SelectExternalDestinationStep
                                        existingDestinationIds={
                                            existingDestinationIds
                                        }
                                        setSelectedPlatform={
                                            setSelectedPlatform
                                        }
                                        advanceToPlatformSettings={(
                                            destination?: DestinationDescriptor
                                        ) => {
                                            if (destination) {
                                                setSelectedDestinationId(
                                                    destination.id
                                                );
                                                setHasAddedPlatformSpecificSettings(
                                                    true
                                                );
                                            }
                                            setActiveStep(
                                                AddDestinationsModalSteps.PlatformSettings
                                            );
                                        }}
                                    />
                                )
                            },
                            {
                                id: AddDestinationsModalSteps.PlatformSettings,
                                component: (
                                    <PlatformSettings
                                        selectedPlatform={selectedPlatform}
                                        setHasAddedPlatformSpecificSettings={
                                            setHasAddedPlatformSpecificSettings
                                        }
                                        setOnAdvanceStep={setOnAdvanceStep}
                                        selectedDestinationId={
                                            selectedDestinationId
                                        }
                                        advanceStep={advanceStep}
                                        setOnDismiss={setOnDismiss}
                                        setCurrentFacebookEdge={
                                            setCurrentFacebookEdge
                                        }
                                    />
                                )
                            },
                            {
                                id: AddDestinationsModalSteps.Processing,
                                component: (
                                    <AddDestinationsProcessingStep
                                        destinations={
                                            newlySelectedDestinations
                                        }
                                        broadcast={mappedBroadcast}
                                        onStatusChange={setSubmissionStatus}
                                        onSettled={refetchBroadcast}
                                    />
                                )
                            }
                        ]}
                    />
                )}
            </div>
        </ModalBase>
    );
};
